import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

import { useUserData } from './UserDataContext';
import { useRealtime } from './RealtimeContext';
import { getAllExamData, saveUserExamPreferences, getUserExamPreferences, updateUserProfile } from '../firebase/collections';

const ExamContext = createContext();

export function useExam() {
  return useContext(ExamContext);
}

export function ExamProvider({ children }) {
  const { currentUser, userData } = useRealtime();
  const userProfile = userData?.preferences || null;
  const { userPreferences, updateUserPreferences } = useUserData();
  const [selectedExams, setSelectedExams] = useState([]);
  const [activeExam, setActiveExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableExams, setAvailableExams] = useState([]);
  const [examsLoading, setExamsLoading] = useState(true);
  const [hasLoadedFromStorage, setHasLoadedFromStorage] = useState(false);
  const selectedExamsRef = useRef([]);

  // Load available exams from Firebase
  useEffect(() => {
    const loadAvailableExams = async () => {
      try {
        setExamsLoading(true);
        const result = await getAllExamData();
        
        if (result.success && result.data) {
          // Convert the exam data object to an array format
          const examsArray = Object.entries(result.data).map(([id, examData]) => ({
            id,
            name: examData.name || id.toUpperCase(),
            fullName: examData.fullName || examData.name || id.toUpperCase(),
            description: examData.description || `Preparation for ${examData.name || id.toUpperCase()}`,
            color: examData.color || 'bg-gray-500',
            textColor: examData.textColor || 'text-gray-600',
            borderColor: examData.borderColor || 'border-gray-200',
            icon: examData.icon || '📚'
          }));
          
          setAvailableExams(examsArray);
          } else {
          // Fallback to comprehensive exam list if Firebase data is not available
          const fallbackExams = [
            { id: 'cat', name: 'CAT', fullName: 'Common Admission Test', description: 'Management entrance exam for IIMs', color: 'bg-blue-500', textColor: 'text-blue-600', borderColor: 'border-blue-200', icon: '🎓' },
            { id: 'gate', name: 'GATE', fullName: 'Graduate Aptitude Test in Engineering', description: 'Engineering postgraduate entrance exam', color: 'bg-green-500', textColor: 'text-green-600', borderColor: 'border-green-200', icon: '⚙️' },
            { id: 'jee', name: 'JEE', fullName: 'Joint Entrance Examination', description: 'Engineering undergraduate entrance exam', color: 'bg-purple-500', textColor: 'text-purple-600', borderColor: 'border-purple-200', icon: '🔬' },
            { id: 'neet', name: 'NEET', fullName: 'National Eligibility cum Entrance Test', description: 'Medical entrance examination', color: 'bg-red-500', textColor: 'text-red-600', borderColor: 'border-red-200', icon: '🏥' },
            { id: 'upsc', name: 'UPSC', fullName: 'Union Public Service Commission', description: 'Civil services examination', color: 'bg-orange-500', textColor: 'text-orange-600', borderColor: 'border-orange-200', icon: '🏛️' },
            { id: 'banking', name: 'Banking', fullName: 'Banking Examinations', description: 'IBPS, SBI, and other banking exams', color: 'bg-indigo-500', textColor: 'text-indigo-600', borderColor: 'border-indigo-200', icon: '🏦' },
            { id: 'ssc_cgl', name: 'SSC-CGL', fullName: 'Staff Selection Commission Combined Graduate Level', description: 'Government job examination for graduates', color: 'bg-teal-500', textColor: 'text-teal-600', borderColor: 'border-teal-200', icon: '👔' },
            { id: 'ssc_chsl', name: 'SSC-CHSL', fullName: 'Staff Selection Commission Combined Higher Secondary Level', description: 'Government job examination for 12th pass', color: 'bg-cyan-500', textColor: 'text-cyan-600', borderColor: 'border-cyan-200', icon: '📋' },
            { id: 'rrb_ntpc', name: 'RRB NTPC', fullName: 'Railway Recruitment Board Non-Technical Popular Categories', description: 'Railway recruitment examination', color: 'bg-amber-500', textColor: 'text-amber-600', borderColor: 'border-amber-200', icon: '🚂' },
            { id: 'ctet', name: 'CTET', fullName: 'Central Teacher Eligibility Test', description: 'Teacher eligibility examination', color: 'bg-emerald-500', textColor: 'text-emerald-600', borderColor: 'border-emerald-200', icon: '📚' },
            { id: 'net', name: 'NET', fullName: 'National Eligibility Test', description: 'Assistant Professor and JRF examination', color: 'bg-violet-500', textColor: 'text-violet-600', borderColor: 'border-violet-200', icon: '🎓' },
            { id: 'clat', name: 'CLAT', fullName: 'Common Law Admission Test', description: 'Law entrance examination', color: 'bg-slate-500', textColor: 'text-slate-600', borderColor: 'border-slate-200', icon: '⚖️' },
            { id: 'ailet', name: 'AILET', fullName: 'All India Law Entrance Test', description: 'National Law University entrance exam', color: 'bg-stone-500', textColor: 'text-stone-600', borderColor: 'border-stone-200', icon: '🏛️' },
            { id: 'nda', name: 'NDA', fullName: 'National Defence Academy', description: 'Defence services entrance examination', color: 'bg-sky-500', textColor: 'text-sky-600', borderColor: 'border-sky-200', icon: '🎖️' },
            { id: 'cds', name: 'CDS', fullName: 'Combined Defence Services', description: 'Defence services examination', color: 'bg-blue-600', textColor: 'text-blue-700', borderColor: 'border-blue-300', icon: '🛡️' }
          ];
          setAvailableExams(fallbackExams);
        }
      } catch (error) {
        // Use fallback exams on error
        const fallbackExams = [
          { id: 'cat', name: 'CAT', fullName: 'Common Admission Test', description: 'Management entrance exam for IIMs', color: 'bg-blue-500', textColor: 'text-blue-600', borderColor: 'border-blue-200', icon: '🎓' },
          { id: 'gate', name: 'GATE', fullName: 'Graduate Aptitude Test in Engineering', description: 'Engineering postgraduate entrance exam', color: 'bg-green-500', textColor: 'text-green-600', borderColor: 'border-green-200', icon: '⚙️' },
          { id: 'jee', name: 'JEE', fullName: 'Joint Entrance Examination', description: 'Engineering undergraduate entrance exam', color: 'bg-purple-500', textColor: 'text-purple-600', borderColor: 'border-purple-200', icon: '🔬' },
          { id: 'neet', name: 'NEET', fullName: 'National Eligibility cum Entrance Test', description: 'Medical entrance examination', color: 'bg-red-500', textColor: 'text-red-600', borderColor: 'border-red-200', icon: '🏥' },
          { id: 'upsc', name: 'UPSC', fullName: 'Union Public Service Commission', description: 'Civil services examination', color: 'bg-orange-500', textColor: 'text-orange-600', borderColor: 'border-orange-200', icon: '🏛️' },
          { id: 'banking', name: 'Banking', fullName: 'Banking Examinations', description: 'IBPS, SBI, and other banking exams', color: 'bg-indigo-500', textColor: 'text-indigo-600', borderColor: 'border-indigo-200', icon: '🏦' },
          { id: 'ssc_cgl', name: 'SSC-CGL', fullName: 'Staff Selection Commission Combined Graduate Level', description: 'Government job examination for graduates', color: 'bg-teal-500', textColor: 'text-teal-600', borderColor: 'border-teal-200', icon: '👔' },
          { id: 'ssc_chsl', name: 'SSC-CHSL', fullName: 'Staff Selection Commission Combined Higher Secondary Level', description: 'Government job examination for 12th pass', color: 'bg-cyan-500', textColor: 'text-cyan-600', borderColor: 'border-cyan-200', icon: '📋' },
          { id: 'rrb_ntpc', name: 'RRB NTPC', fullName: 'Railway Recruitment Board Non-Technical Popular Categories', description: 'Railway recruitment examination', color: 'bg-amber-500', textColor: 'text-amber-600', borderColor: 'border-amber-200', icon: '🚂' },
          { id: 'ctet', name: 'CTET', fullName: 'Central Teacher Eligibility Test', description: 'Teacher eligibility examination', color: 'bg-emerald-500', textColor: 'text-emerald-600', borderColor: 'border-emerald-200', icon: '📚' },
          { id: 'net', name: 'NET', fullName: 'National Eligibility Test', description: 'Assistant Professor and JRF examination', color: 'bg-violet-500', textColor: 'text-violet-600', borderColor: 'border-violet-200', icon: '🎓' },
          { id: 'clat', name: 'CLAT', fullName: 'Common Law Admission Test', description: 'Law entrance examination', color: 'bg-slate-500', textColor: 'text-slate-600', borderColor: 'border-slate-200', icon: '⚖️' },
          { id: 'ailet', name: 'AILET', fullName: 'All India Law Entrance Test', description: 'National Law University entrance exam', color: 'bg-stone-500', textColor: 'text-stone-600', borderColor: 'border-stone-200', icon: '🏛️' },
          { id: 'nda', name: 'NDA', fullName: 'National Defence Academy', description: 'Defence services entrance examination', color: 'bg-sky-500', textColor: 'text-sky-600', borderColor: 'border-sky-200', icon: '🎖️' },
          { id: 'cds', name: 'CDS', fullName: 'Combined Defence Services', description: 'Defence services examination', color: 'bg-blue-600', textColor: 'text-blue-700', borderColor: 'border-blue-300', icon: '🛡️' }
        ];
        setAvailableExams(fallbackExams);
      } finally {
        setExamsLoading(false);
      }
    };

    loadAvailableExams();
  }, []);

  // Load from localStorage first (immediate availability)
  useEffect(() => {
    if (!hasLoadedFromStorage) {
      try {
        const storedExams = localStorage.getItem('selectedExams');
        const storedActiveExam = localStorage.getItem('activeExam');
        
        if (storedExams) {
          const parsedExams = JSON.parse(storedExams);
          if (Array.isArray(parsedExams) && parsedExams.length > 0) {
            setSelectedExams(parsedExams);
            selectedExamsRef.current = parsedExams;
            
            // Restore active exam if available
            if (storedActiveExam) {
              const parsedActiveExam = JSON.parse(storedActiveExam);
              setActiveExam(parsedActiveExam);
            } else if (parsedExams.length > 0) {
              setActiveExam(parsedExams[0]);
            }
            
            setLoading(false);
            setHasLoadedFromStorage(true);
          }
        }
      } catch (error) {
        // Error loading from localStorage, continue to Firebase
      }
    }
  }, [hasLoadedFromStorage]);

  // Load user's selected exams from preferences (Firebase)
  useEffect(() => {
    const loadExamPreferences = async () => {
      // Skip if we already have exams loaded and they match Firebase data
      // This prevents unnecessary resets during navigation
      if (selectedExamsRef.current.length > 0 && hasLoadedFromStorage) {
        // Only reload if Firebase data is different
        const firebaseExams = userData?.preferences?.selectedExams || 
                             userPreferences?.selectedExams || 
                             userProfile?.selectedExams;
        
        if (firebaseExams && Array.isArray(firebaseExams) && firebaseExams.length > 0) {
          // Check if exams are different
          const currentIds = selectedExamsRef.current.map(e => e.id).sort().join(',');
          const firebaseIds = firebaseExams.map(e => e.id || e).sort().join(',');
          
          // If they're the same, don't reset
          if (currentIds === firebaseIds) {
            setLoading(false);
            return;
          }
        }
      }

      try {
        // Helper function to restore exams and active exam
        const restoreExams = (userExams, preferences) => {
          setSelectedExams(userExams);
          selectedExamsRef.current = userExams;
          
          // Save to localStorage
          localStorage.setItem('selectedExams', JSON.stringify(userExams));
          
          // Restore active exam from preferences if available, otherwise use first exam
          if (preferences?.activeExam) {
            const savedActiveExam = userExams.find(e => e.id === preferences.activeExam.id) || preferences.activeExam;
            setActiveExam(savedActiveExam);
            localStorage.setItem('activeExam', JSON.stringify(savedActiveExam));
          } else if (userExams.length > 0) {
            const firstExam = userExams[0];
            setActiveExam(firstExam);
            localStorage.setItem('activeExam', JSON.stringify(firstExam));
          }
          
          setLoading(false);
          setHasLoadedFromStorage(true);
        };

        // First check if userData has preferences with selectedExams
        if (userData?.preferences?.selectedExams && userData.preferences.selectedExams.length > 0) {
          const userExams = userData.preferences.selectedExams || [];
          restoreExams(userExams, userData.preferences);
        } else if (userData?.examPreferences?.selectedExams && userData.examPreferences.selectedExams.length > 0) {
          // Check exam preferences from RealtimeContext
          const userExams = userData.examPreferences.selectedExams || [];
          restoreExams(userExams, userData.examPreferences);
        } else if (userPreferences && userPreferences.selectedExams) {
          const userExams = userPreferences.selectedExams || [];
          restoreExams(userExams, userPreferences);
        } else if (userProfile && userProfile.selectedExams) {
          // Fallback to userProfile for backward compatibility
          const userExams = userProfile.selectedExams || [];
          restoreExams(userExams, userProfile);
        } else if (userProfile && userProfile.preferences?.selectedExams) {
          // Check userProfile preferences for Google sign-in users
          const userExams = userProfile.preferences.selectedExams || [];
          restoreExams(userExams, userProfile.preferences);
        } else if (currentUser) {
          // Try to load from Firebase userExamPreferences collection
          try {
            const result = await getUserExamPreferences(currentUser.uid);
            if (result.success && result.data && result.data.selectedExams) {
              const userExams = result.data.selectedExams || [];
              restoreExams(userExams, result.data);
            } else {
              // No exam preferences found in Firebase, but keep localStorage if exists
              if (!hasLoadedFromStorage) {
                setLoading(false);
              }
            }
          } catch (firebaseError) {
            // Error loading exam preferences from Firebase, keep localStorage if exists
            if (!hasLoadedFromStorage) {
              setLoading(false);
            }
          }
        } else {
          // If no user data available, keep localStorage if exists
          if (!hasLoadedFromStorage) {
            setLoading(false);
          }
        }
      } catch (error) {
        // Error loading exam preferences, keep localStorage if exists
        if (!hasLoadedFromStorage) {
          setLoading(false);
        }
      }
    };

    // Only load from Firebase if we have a user or if we haven't loaded from storage yet
    if (currentUser || !hasLoadedFromStorage) {
      loadExamPreferences();
    }
  }, [userPreferences, userProfile, userData, currentUser, hasLoadedFromStorage]);

  // Update user profile when exams change
  const updateSelectedExams = async (exams) => {
    setSelectedExams(exams);
    selectedExamsRef.current = exams;
    
    // Always save to localStorage for immediate persistence
    try {
      localStorage.setItem('selectedExams', JSON.stringify(exams));
      if (activeExam) {
        localStorage.setItem('activeExam', JSON.stringify(activeExam));
      }
    } catch (error) {
      // Error saving to localStorage
    }
    
    if (currentUser) {
      try {
        // Prepare preferences data with selected exams and active exam
        const preferencesData = {
          selectedExams: exams,
          lastUpdated: new Date().toISOString()
        };
        
        // Include active exam if it exists and is in the selected exams
        if (activeExam && exams.find(e => e.id === activeExam.id)) {
          preferencesData.activeExam = activeExam;
        }
        
        // Save to userPreferences collection (primary - this is what RealtimeContext listens to)
        if (updateUserPreferences) {
          await updateUserPreferences(preferencesData);
        }
        
        // Save to Firebase userExamPreferences collection (for backward compatibility)
        await saveUserExamPreferences(currentUser.uid, preferencesData);
        
        // Also update user profile for backward compatibility
        await updateUserProfile(currentUser.uid, {
          preferences: preferencesData
        });
        
      } catch (error) {
        // Error saving exam preferences
        console.error('Error saving exam preferences:', error);
      }
    }
  };

  // Add exam to user's selection
  const addExam = async (examId) => {
    const exam = availableExams.find(e => e.id === examId);
    if (exam && !selectedExams.find(e => e.id === examId)) {
      const newExams = [...selectedExams, exam];
      await updateSelectedExams(newExams);
      
      // Set as active exam if it's the first one
      if (selectedExams.length === 0) {
        setActiveExam(exam);
        // Save active exam to localStorage
        try {
          localStorage.setItem('activeExam', JSON.stringify(exam));
        } catch (error) {
          // Error saving to localStorage
        }
      }
    }
  };

  // Remove exam from user's selection
  const removeExam = async (examId) => {
    const newExams = selectedExams.filter(e => e.id !== examId);
    await updateSelectedExams(newExams);
    
    // Update active exam if the removed exam was active
    if (activeExam?.id === examId) {
      const newActiveExam = newExams.length > 0 ? newExams[0] : null;
      setActiveExam(newActiveExam);
      // Update localStorage
      try {
        if (newActiveExam) {
          localStorage.setItem('activeExam', JSON.stringify(newActiveExam));
        } else {
          localStorage.removeItem('activeExam');
        }
      } catch (error) {
        // Error saving to localStorage
      }
    }
  };

  // Switch active exam
  const switchActiveExam = async (examId) => {
    const exam = selectedExams.find(e => e.id === examId);
    if (exam) {
      setActiveExam(exam);
      // Save to localStorage
      try {
        localStorage.setItem('activeExam', JSON.stringify(exam));
      } catch (error) {
        // Error saving to localStorage
      }
      
      // Also save to Firebase if user is logged in
      if (currentUser && updateUserPreferences) {
        try {
          await updateUserPreferences({
            activeExam: exam,
            lastUpdated: new Date().toISOString()
          });
        } catch (error) {
          // Error saving active exam to Firebase
          console.error('Error saving active exam:', error);
        }
      }
    }
  };

  // Get exam by ID
  const getExamById = (examId) => {
    return availableExams.find(e => e.id === examId);
  };

  const value = {
    selectedExams,
    activeExam,
    availableExams,
    loading,
    examsLoading,
    addExam,
    removeExam,
    switchActiveExam,
    getExamById,
    updateSelectedExams
  };

  return (
    <ExamContext.Provider value={value}>
      {children}
    </ExamContext.Provider>
  );
}
